import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Pill, ShoppingCart, AlertTriangle, CheckCircle2,
  Package, RefreshCw, Search, Plus, X,
} from 'lucide-react';
import { format } from 'date-fns';
import { api } from '@/lib/api';
import { clsx } from 'clsx';

type Tab = 'queue' | 'stock' | 'expiry';

interface DrugBatch {
  batch_id:    string;
  drug_id:     string;
  batch_number: string;
  expiry_date: string;
  quantity:    number;
  mrp:         number;
  generic_name: string;
  brand_name:  string | null;
}

interface DispenseItem {
  drug_name:     string;
  prescribed_qty: number;
  quantity:      number;
  batch_id:      string;
  drug_id:       string;
  mrp:           number;
  batches:       DrugBatch[];
}

const FORMULATIONS = [
  'tablet','capsule','syrup','injection','drops',
  'cream','ointment','inhaler','suspension','powder','other',
] as const;

// ── Stat card ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, color, icon: Icon }: any) {
  return (
    <div className={clsx('border rounded-xl p-4 shadow-sm', color)}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-2xl font-bold text-navy">{value ?? 0}</div>
          <div className="text-xs text-gray-500 mt-0.5">{label}</div>
        </div>
        <Icon size={20} className="text-gray-400"/>
      </div>
    </div>
  );
}

// ── Add Stock Modal ────────────────────────────────────────────────────────────
function AddStockModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [form, setForm] = useState({
    generic_name: '', brand_name: '', formulation: 'tablet', strength: '',
    batch_number: '', expiry_date: '', quantity: '', mrp: '',
    unit_cost: '', gst_rate: '12', location: '',
  });
  const [apiError, setApiError] = useState('');

  function set(field: string, val: string) {
    setForm(prev => ({ ...prev, [field]: val }));
  }

  const mutation = useMutation({
    mutationFn: () => api.post('/pharmacy/receive', {
      generic_name: form.generic_name,
      ...(form.brand_name  ? { brand_name:  form.brand_name }          : {}),
      formulation:  form.formulation,
      ...(form.strength    ? { strength:    form.strength }             : {}),
      batch_number: form.batch_number,
      expiry_date:  form.expiry_date,
      quantity:     parseFloat(form.quantity),
      mrp:          parseFloat(form.mrp),
      unit_cost:    form.unit_cost ? parseFloat(form.unit_cost) : 0,
      gst_rate:     parseFloat(form.gst_rate || '12'),
      ...(form.location    ? { location:    form.location }             : {}),
    }),
    onSuccess: () => { onSuccess(); onClose(); },
    onError: (err: any) => setApiError(err.response?.data?.error ?? err.message),
  });

  const valid = form.generic_name && form.batch_number && form.expiry_date &&
                parseFloat(form.quantity) > 0 && parseFloat(form.mrp) >= 0;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-navy">Receive Stock</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16}/></button>
        </div>

        <div className="p-5 space-y-4">
          {apiError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{apiError}</div>
          )}

          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Drug Details</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs text-gray-500 font-medium">Generic Name *</label>
              <input
                value={form.generic_name}
                onChange={e => set('generic_name', e.target.value)}
                placeholder="e.g. Paracetamol"
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-navy/40"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">Brand Name</label>
              <input
                value={form.brand_name}
                onChange={e => set('brand_name', e.target.value)}
                placeholder="e.g. Crocin"
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-navy/40"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">Strength</label>
              <input
                value={form.strength}
                onChange={e => set('strength', e.target.value)}
                placeholder="e.g. 500mg"
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-navy/40"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-500 font-medium">Formulation *</label>
              <select
                value={form.formulation}
                onChange={e => set('formulation', e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-navy/40 bg-white capitalize"
              >
                {FORMULATIONS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>

          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-2">Batch Details</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 font-medium">Batch Number *</label>
              <input
                value={form.batch_number}
                onChange={e => set('batch_number', e.target.value)}
                placeholder="e.g. BT2024001"
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-navy/40"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">Expiry Date *</label>
              <input
                type="date"
                value={form.expiry_date}
                onChange={e => set('expiry_date', e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-navy/40"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">Quantity *</label>
              <input
                type="number" min="1"
                value={form.quantity}
                onChange={e => set('quantity', e.target.value)}
                placeholder="0"
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-navy/40"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">MRP (₹) *</label>
              <input
                type="number" min="0" step="0.01"
                value={form.mrp}
                onChange={e => set('mrp', e.target.value)}
                placeholder="0.00"
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-navy/40"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">Unit Cost (₹)</label>
              <input
                type="number" min="0" step="0.01"
                value={form.unit_cost}
                onChange={e => set('unit_cost', e.target.value)}
                placeholder="0.00"
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-navy/40"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">GST %</label>
              <input
                type="number" min="0" max="28"
                value={form.gst_rate}
                onChange={e => set('gst_rate', e.target.value)}
                placeholder="12"
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-navy/40"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-gray-500 font-medium">Storage Location</label>
              <input
                value={form.location}
                onChange={e => set('location', e.target.value)}
                placeholder="e.g. Shelf A-3"
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-navy/40"
              />
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-100 flex gap-3 justify-end sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!valid || mutation.isPending}
            className="px-5 py-2 rounded-xl bg-navy text-white text-sm hover:bg-navy/90 transition disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving…' : 'Add to Stock'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Dispense Modal ─────────────────────────────────────────────────────────────
function DispenseModal({ item, onClose }: { item: any; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [dispItems, setDispItems] = useState<DispenseItem[]>([]);
  const [initialised, setInitialised] = useState(false);
  const [notes, setNotes] = useState('');
  const [apiError, setApiError] = useState('');

  const drugNames: string[] = (item.items ?? []).map((d: any) => d.drug_name);

  const { data: batchesMap, isLoading } = useQuery({
    queryKey: ['dispense-batches', item.prescription_id],
    queryFn: async () => {
      const results = await Promise.all(
        drugNames.map(name =>
          api.get(`/pharmacy/batches?q=${encodeURIComponent(name)}`).then(r => r.data as DrugBatch[])
        )
      );
      return Object.fromEntries(drugNames.map((name, i) => [name, results[i]]));
    },
    enabled: drugNames.length > 0,
  });

  useEffect(() => {
    if (!batchesMap || initialised) return;
    const items: DispenseItem[] = (item.items ?? []).map((drug: any) => {
      const batches: DrugBatch[] = batchesMap[drug.drug_name] ?? [];
      const best = batches[0];
      return {
        drug_name:      drug.drug_name,
        prescribed_qty: drug.quantity ?? 1,
        quantity:       drug.quantity ?? 1,
        batch_id:       best?.batch_id ?? '',
        drug_id:        best?.drug_id  ?? '',
        mrp:            best?.mrp      ?? 0,
        batches,
      };
    });
    setDispItems(items);
    setInitialised(true);
  }, [batchesMap, initialised, item.items]);

  const mutation = useMutation({
    mutationFn: () => {
      const valid = dispItems.filter(i => i.batch_id && i.quantity > 0);
      if (valid.length === 0) throw new Error('Select at least one batch to dispense');
      return api.post('/pharmacy/dispense', {
        prescription_id: item.prescription_id,
        patient_id:      item.patient_id,
        items: valid.map(i => ({
          drug_id:   i.drug_id,
          drug_name: i.drug_name,
          batch_id:  i.batch_id,
          quantity:  i.quantity,
          unit:      'tablet',
          mrp:       i.mrp,
        })),
        ...(notes ? { notes } : {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-queue'] });
      onClose();
    },
    onError: (err: any) => {
      const d = err.response?.data;
      setApiError(d?.details?.join(', ') ?? d?.error ?? err.message);
    },
  });

  function updateItem(idx: number, field: keyof DispenseItem, val: any) {
    setDispItems(prev => {
      const next = [...prev];
      const row  = { ...next[idx], [field]: val };
      if (field === 'batch_id') {
        const b = next[idx].batches.find(b => b.batch_id === val);
        if (b) { row.drug_id = b.drug_id; row.mrp = b.mrp; }
      }
      next[idx] = row;
      return next;
    });
  }

  const total      = dispItems.reduce((s, i) => s + i.mrp * i.quantity, 0);
  const canDispense = dispItems.some(i => i.batch_id && i.quantity > 0);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-lg font-bold text-navy">Dispense Prescription</h2>
          <p className="text-sm text-gray-400 mt-0.5">{item.patient_uid} · Dr. {item.doctor_name}</p>
        </div>

        <div className="p-5 space-y-3">
          {apiError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{apiError}</div>
          )}

          {isLoading ? (
            <div className="text-center py-10 text-gray-400 text-sm">Loading available stock…</div>
          ) : (
            <>
              {/* Headers */}
              <div className="grid grid-cols-12 gap-2 px-3 text-xs font-semibold text-gray-400 uppercase">
                <div className="col-span-4">Drug</div>
                <div className="col-span-4">Batch (FEFO)</div>
                <div className="col-span-2 text-center">Qty</div>
                <div className="col-span-2 text-right">Amount</div>
              </div>

              {dispItems.map((di, idx) => (
                <div
                  key={idx}
                  className={clsx(
                    'grid grid-cols-12 gap-2 p-3 rounded-xl items-center',
                    di.batch_id ? 'bg-gray-50' : 'bg-red-50 border border-red-200'
                  )}
                >
                  <div className="col-span-4">
                    <div className="font-medium text-gray-800 text-sm leading-tight">{di.drug_name}</div>
                    <div className="text-xs text-gray-400 mt-0.5">Rx: {di.prescribed_qty}</div>
                  </div>

                  <div className="col-span-4">
                    {di.batches.length === 0 ? (
                      <span className="text-xs text-red-500 font-medium">Out of stock</span>
                    ) : (
                      <select
                        value={di.batch_id}
                        onChange={e => updateItem(idx, 'batch_id', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs outline-none bg-white"
                      >
                        {di.batches.map(b => (
                          <option key={b.batch_id} value={b.batch_id}>
                            {b.batch_number} · exp {new Date(b.expiry_date).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })} · {b.quantity} left
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="col-span-2">
                    <input
                      type="number" min="0"
                      value={di.quantity}
                      onChange={e => updateItem(idx, 'quantity', parseInt(e.target.value) || 0)}
                      className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs text-center outline-none"
                    />
                  </div>

                  <div className="col-span-2 text-right text-xs font-medium text-gray-600">
                    ₹{(di.mrp * di.quantity).toFixed(0)}
                  </div>
                </div>
              ))}

              <div className="flex justify-between items-center px-3 pt-2 border-t border-gray-100">
                <span className="text-sm font-medium text-gray-500">Total (MRP)</span>
                <span className="text-lg font-bold text-navy">₹{total.toFixed(0)}</span>
              </div>

              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Notes (optional)"
                rows={2}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none resize-none"
              />
            </>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 flex gap-3 justify-end sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!canDispense || mutation.isPending || isLoading}
            className="px-5 py-2 rounded-xl bg-navy text-white text-sm hover:bg-navy/90 transition disabled:opacity-50"
          >
            {mutation.isPending ? 'Dispensing…' : 'Confirm Dispense'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function PharmacyPage() {
  const queryClient = useQueryClient();
  const [tab, setTab]         = useState<Tab>('queue');
  const [drugSearch, setDrugSearch] = useState('');
  const [dispensing, setDispensing] = useState<any>(null);
  const [addingStock, setAddingStock] = useState(false);

  const { data: queueData, isLoading: queueLoading, refetch: refetchQueue } = useQuery({
    queryKey:  ['pharmacy-queue'],
    queryFn:   () => api.get('/pharmacy/queue').then(r => r.data),
    refetchInterval: 60_000,
  });

  const { data: stockData, isLoading: stockLoading } = useQuery({
    queryKey:  ['pharmacy-stock'],
    queryFn:   () => api.get('/pharmacy/stock').then(r => r.data),
    enabled:   tab === 'stock',
    staleTime: 60_000,
  });

  const { data: expiryData } = useQuery({
    queryKey: ['pharmacy-expiry'],
    queryFn:  () => api.get('/pharmacy/expiry-alerts').then(r => r.data),
    enabled:  tab === 'expiry',
  });

  const queue  = queueData?.queue ?? [];
  const stock  = (stockData ?? []).filter((s: any) =>
    !drugSearch ||
    s.generic_name?.toLowerCase().includes(drugSearch.toLowerCase()) ||
    s.brand_name?.toLowerCase().includes(drugSearch.toLowerCase())
  );
  const expiry = expiryData ?? [];

  const expiringCritical = expiry.filter((e: any) => e.days_to_expiry <= 7).length;
  const expiringSoon     = expiry.filter((e: any) => e.days_to_expiry > 7 && e.days_to_expiry <= 30).length;

  const TABS: { id: Tab; label: string; count?: number }[] = [
    { id: 'queue',  label: 'Dispensing Queue', count: queue.length },
    { id: 'stock',  label: 'Stock Inventory' },
    { id: 'expiry', label: 'Expiry Alerts',    count: expiringCritical + expiringSoon },
  ];

  return (
    <div className="space-y-6 p-6 max-w-screen-xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy">Pharmacy</h1>
          <p className="text-sm text-gray-400 mt-0.5">Dispensing &amp; stock management</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAddingStock(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-navy text-white text-sm hover:bg-navy/90 transition"
          >
            <Plus size={14}/>
            Receive Stock
          </button>
          <button
            onClick={() => refetchQueue()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
          >
            <RefreshCw size={14} className={queueLoading ? 'animate-spin' : ''}/>
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="Pending Today"     value={queue.length}           color="bg-white border-gray-200"       icon={ShoppingCart}/>
        <StatCard label="Expiring ≤7 Days"  value={expiringCritical}       color="bg-red-50 border-red-200"       icon={AlertTriangle}/>
        <StatCard label="Expiring ≤30 Days" value={expiringSoon}           color="bg-yellow-50 border-yellow-200" icon={Package}/>
        <StatCard label="Stock Lines"       value={stockData?.length ?? '—'} color="bg-white border-gray-200"     icon={Pill}/>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px flex items-center gap-1.5',
              tab === t.id
                ? 'border-navy text-navy'
                : 'border-transparent text-gray-400 hover:text-gray-600'
            )}
          >
            {t.label}
            {t.count != null && t.count > 0 && (
              <span className={clsx(
                'px-1.5 py-0.5 rounded-full text-xs font-bold',
                t.id === 'expiry' && expiringCritical > 0
                  ? 'bg-red-100 text-red-600'
                  : 'bg-navy/10 text-navy'
              )}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Dispensing Queue ──────────────────────────────────────────── */}
      {tab === 'queue' && (
        <div className="space-y-3">
          {queueLoading ? (
            <div className="text-center py-16 text-gray-400">Loading queue…</div>
          ) : queue.length === 0 ? (
            <div className="text-center py-16">
              <CheckCircle2 size={40} className="mx-auto text-green-300 mb-3"/>
              <p className="text-gray-400 text-sm font-medium">Queue clear for today</p>
              <p className="text-gray-300 text-xs mt-1">All prescriptions dispensed</p>
            </div>
          ) : queue.map((item: any) => (
            <div key={item.prescription_id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-semibold text-navy text-sm">{item.patient_uid}</span>
                    <span className="text-xs text-gray-400">{item.patient_name_enc}</span>
                    {item.is_handwritten && (
                      <span className={clsx(
                        'px-2 py-0.5 text-xs rounded-lg font-medium',
                        item.ocr_status === 'completed'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      )}>
                        {item.ocr_status === 'completed' ? 'OCR verified' : 'Handwritten'}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(item.items ?? []).slice(0, 4).map((drug: any, i: number) => (
                      <span key={i} className="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs">
                        {drug.drug_name} {drug.dose}
                      </span>
                    ))}
                    {(item.items ?? []).length > 4 && (
                      <span className="px-2.5 py-1 bg-gray-100 text-gray-400 rounded-lg text-xs">
                        +{item.items.length - 4} more
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-400">Dr. {item.doctor_name}</div>
                </div>

                <button
                  onClick={() => setDispensing(item)}
                  className="flex-shrink-0 px-4 py-2 bg-navy text-white text-sm rounded-xl hover:bg-navy/90 transition flex items-center gap-2"
                >
                  <ShoppingCart size={14}/>
                  Dispense
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Stock Inventory ───────────────────────────────────────────── */}
      {tab === 'stock' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2.5 shadow-sm">
            <Search size={15} className="text-gray-400 flex-shrink-0"/>
            <input
              value={drugSearch}
              onChange={e => setDrugSearch(e.target.value)}
              placeholder="Search drug name…"
              className="flex-1 text-sm outline-none text-gray-700 placeholder:text-gray-300"
            />
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Drug</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Batch</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Expiry</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Qty</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-400 uppercase">MRP</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400 uppercase">Location</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {stockLoading ? (
                  <tr><td colSpan={6} className="text-center py-12 text-gray-400">Loading…</td></tr>
                ) : stock.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12">
                      <Package size={36} className="mx-auto text-gray-300 mb-3"/>
                      <p className="text-gray-400 text-sm">No stock found</p>
                      <p className="text-gray-300 text-xs mt-1">Click "Receive Stock" to add drugs</p>
                    </td>
                  </tr>
                ) : stock.map((s: any) => {
                  const expDate  = new Date(s.expiry_date);
                  const daysLeft = Math.ceil((expDate.getTime() - Date.now()) / 86400000);
                  return (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800 text-sm">{s.generic_name}</div>
                        {s.brand_name && (
                          <div className="text-xs text-gray-400">{s.brand_name} · {s.formulation} {s.strength}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">{s.batch_number}</td>
                      <td className="px-4 py-3">
                        <span className={clsx('text-xs font-medium',
                          daysLeft <= 7 ? 'text-red-600' : daysLeft <= 30 ? 'text-yellow-600' : 'text-gray-500'
                        )}>
                          {expDate.toLocaleDateString('en-IN')}
                          {daysLeft <= 30 && <span className="ml-1">({daysLeft}d)</span>}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-700">{s.quantity}</td>
                      <td className="px-4 py-3 text-right text-gray-600 text-sm">₹{s.mrp}</td>
                      <td className="px-4 py-3 text-xs text-gray-400">{s.location ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Expiry Alerts ─────────────────────────────────────────────── */}
      {tab === 'expiry' && (
        <div className="space-y-3">
          {expiry.length === 0 ? (
            <div className="text-center py-16">
              <CheckCircle2 size={40} className="mx-auto text-green-300 mb-3"/>
              <p className="text-gray-400 text-sm">No drugs expiring in the next 60 days</p>
            </div>
          ) : expiry.map((item: any) => (
            <div
              key={item.id}
              className={clsx('border rounded-xl p-4 flex items-center gap-4',
                item.days_to_expiry <= 7 ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
              )}
            >
              <AlertTriangle
                size={18}
                className={item.days_to_expiry <= 7 ? 'text-red-500 flex-shrink-0' : 'text-yellow-500 flex-shrink-0'}
              />
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-gray-800 text-sm">{item.generic_name}</span>
                {item.brand_name && <span className="text-gray-400 text-xs ml-2">{item.brand_name}</span>}
                <div className="text-xs text-gray-500 mt-0.5">
                  Batch {item.batch_number} · Qty: {item.quantity}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className={clsx('font-bold text-sm', item.days_to_expiry <= 7 ? 'text-red-600' : 'text-yellow-600')}>
                  {item.days_to_expiry}d left
                </div>
                <div className="text-xs text-gray-400">
                  {new Date(item.expiry_date).toLocaleDateString('en-IN')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {addingStock && (
        <AddStockModal
          onClose={() => setAddingStock(false)}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: ['pharmacy-stock'] })}
        />
      )}
      {dispensing && (
        <DispenseModal
          item={dispensing}
          onClose={() => setDispensing(null)}
        />
      )}
    </div>
  );
}
